/** IDs de marca white-label (espelha `passbook.partnerName` do legado). */
export type BrandId = 'fixe' | 'webautomacao' | 'goomer';

export interface BrandAssets {
  logotype: {
    light: string;
    dark: string;
    /** Filtro aplicado somente ao logotipo no modo claro. */
    lightFilter?: string;
  };
  icon: {
    /** Ícone colorido — sidebar recolhida, loader e login. */
    brand: string;
    light: string;
    dark: string;
  };
  favicon: {
    brand: string;
    light: string;
    dark: string;
    apple: string;
  };
}

export interface BrandDefinition {
  id: BrandId;
  /** Nome curto da marca (sidebar, aria-label). */
  displayName: string;
  /** Título da página de login / document.title no login. */
  loginTitle: string;
  /** Subtítulo sob "Bem vindo !" no login. */
  loginSubtitle: string;
  /** Prefixo de `document.title` nas telas autenticadas. */
  documentTitle: string;
  metaAuthor: string;
  copyright: string;
  /** Proporção largura/altura do logotipo horizontal. */
  logotypeAspect: number;
  /** Compensa diferenças de área útil entre os arquivos de logotipo. */
  logotypeScale: number;
  /** Tokens HSL da identidade visual, aplicados globalmente pelo BrandProvider. */
  theme: {
    primary: string;
    primaryForeground: string;
    hue: string;
  };
  assets: BrandAssets;
}

/** Cores oficiais da marca FixeCRM (tema do produto; WL não troca o tema CSS). */
export const BRAND_COLORS = {
  primary: '#ffc001',
  baseDark: '#000000',
  baseLight: '#ffffff',
  foregroundLight: '#404040',
} as const;

const FIXE_ASSETS: BrandAssets = {
  logotype: {
    light: '/logotipo-preto.svg',
    dark: '/logotipo-branco.svg',
  },
  icon: {
    brand: '/favicon_amarelo.png',
    light: '/favicon_preto.png',
    dark: '/favicon_branco.png',
  },
  favicon: {
    brand: '/favicon_amarelo.png',
    light: '/favicon_preto.png',
    dark: '/favicon_branco.png',
    apple: '/favicon_amarelo.png',
  },
};

const GOOMER_ASSETS: BrandAssets = {
  logotype: {
    light: '/brands/goomer/goomer_logo.png',
    dark: '/brands/goomer/goomer_logo.png',
    lightFilter: 'brightness(0)',
  },
  icon: {
    brand: '/brands/goomer/logo_goomer_triangulo.png',
    light: '/brands/goomer/logo_goomer_triangulo.png',
    dark: '/brands/goomer/logo_goomer_triangulo.png',
  },
  favicon: {
    brand: '/brands/goomer/favicon_goomer.png',
    light: '/brands/goomer/favicon_goomer.png',
    dark: '/brands/goomer/favicon_goomer.png',
    apple: '/brands/goomer/favicon_goomer.png',
  },
};

const WEBAUTOMACAO_ASSETS: BrandAssets = {
  logotype: {
    light: '/brands/webautomacao/pdvlegal_logo.png',
    dark: '/brands/webautomacao/pdvlegal_logo.png',
  },
  icon: {
    brand: '/brands/webautomacao/logo_fidelidade_legal_triangulo.png',
    light: '/brands/webautomacao/logo_fidelidade_legal_triangulo.png',
    dark: '/brands/webautomacao/logo_fidelidade_legal_triangulo.png',
  },
  favicon: {
    brand: '/brands/webautomacao/favicon_pdvlegal.png',
    light: '/brands/webautomacao/favicon_pdvlegal.png',
    dark: '/brands/webautomacao/favicon_pdvlegal.png',
    apple: '/brands/webautomacao/favicon_pdvlegal.png',
  },
};

/** Catálogo de marcas do painel (paridade com legacy/dash). */
export const BRANDS: Record<BrandId, BrandDefinition> = {
  fixe: {
    id: 'fixe',
    displayName: 'FixeCRM',
    loginTitle: 'FixeCRM - Seus ganhos começam aqui.',
    loginSubtitle: 'Acesse para continuar no Fixe CRM.',
    documentTitle: 'FixeCRM',
    metaAuthor: 'FixeCRM',
    copyright: 'FixeCRM',
    logotypeAspect: 711.41 / 145.54,
    logotypeScale: 1,
    theme: {
      primary: '45 100% 50%', // #ffc001
      primaryForeground: '0 0% 0%',
      hue: '45',
    },
    assets: FIXE_ASSETS,
  },
  webautomacao: {
    id: 'webautomacao',
    displayName: 'Fidelidade Legal',
    loginTitle: 'Fidelidade PDV Legal - Login',
    loginSubtitle: 'Acesse para continuar no Fidelidade PDV Legal.',
    documentTitle: 'Fidelidade Legal',
    metaAuthor: 'Fidelidade Legal',
    copyright: 'Fidelidade PDV Legal',
    logotypeAspect: 3.2,
    logotypeScale: 1,
    theme: {
      primary: '199 89% 48%', // #0ea5e9
      primaryForeground: '0 0% 0%',
      hue: '199',
    },
    assets: WEBAUTOMACAO_ASSETS,
  },
  goomer: {
    id: 'goomer',
    displayName: 'Goomer',
    loginTitle: 'Goomer - Login',
    loginSubtitle: 'Acesse para continuar no Fidelidade Goomer.',
    documentTitle: 'Goomer',
    metaAuthor: 'Goomer',
    copyright: 'Fidelidade Goomer',
    logotypeAspect: 3.2,
    logotypeScale: 1,
    theme: {
      primary: '221 83% 53%', // #2563eb
      primaryForeground: '0 0% 100%',
      hue: '221',
    },
    assets: GOOMER_ASSETS,
  },
};

/** Hosts que forçam marca no login (legado: `req.headers.host`). */
export const BRAND_HOSTS: Record<string, BrandId> = {
  'fidelidade.pdvlegal.com.br': 'webautomacao',
  'loyalty.goomer.app': 'goomer',
};

/** Compat: assets FixeCRM (consumidores que ainda importam `BRAND`). */
export const BRAND = FIXE_ASSETS;

/** Proporção do logotipo horizontal FixeCRM (viewBox SVG). */
export const LOGOTYPE_ASPECT = BRANDS.fixe.logotypeAspect;

export function isPartnerBrandId(value: string | undefined | null): value is BrandId {
  return value === 'webautomacao' || value === 'goomer';
}

/** Resolve marca pelo Host (pré-login e fallback pós-login). */
export function resolveBrandIdFromHost(hostname: string): BrandId {
  const host = hostname.split(':')[0]?.toLowerCase() ?? '';
  return BRAND_HOSTS[host] ?? 'fixe';
}

/**
 * Resolve marca efetiva.
 * Host da Fixe (dash/system) nunca herda o partner da loja inspecionada.
 * Em host white-label, o partner do passbook ainda pode trocar a pele.
 */
export function resolveBrandId(options: {
  hostname?: string;
  partner?: boolean;
  partnerName?: string | null;
}): BrandId {
  const hostBrand = options.hostname ? resolveBrandIdFromHost(options.hostname) : 'fixe';
  if (!options.hostname || hostBrand === 'fixe') return 'fixe';
  if (options.partner && isPartnerBrandId(options.partnerName)) {
    return options.partnerName;
  }
  return hostBrand;
}

export function getBrand(id: BrandId): BrandDefinition {
  return BRANDS[id];
}
